import React, { useEffect, useId, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { COPY_STEPUP, TOTP_CODE_LENGTH, normalizeTotpInput } from "../lib/stepUp";

/**
 * Modal de step-up 2FA (FS-035, copy C3/C4/C5/C6).
 *
 * REUTILIZÁVEL de propósito: hoje só a emissão de credencial pede step-up, mas
 * a próxima ação consequente (revogação em massa, troca de dados bancários)
 * herda o MESMO componente — o que muda é a `action` que o chamador manda ao
 * backend, nunca o diálogo.
 *
 * Este componente NÃO fala com a API. Ele coleta o código e devolve em
 * `onSubmit(code)`; quem orquestra (obter a prova → refazer a ação com o
 * header) é a tela, que é quem tem o formulário para preservar. Assim o erro
 * pode vir tanto do `/auth/2fa/step-up` quanto do próprio create, e nos dois
 * casos aparece INLINE, sem fechar o modal.
 *
 * A11y (spec dura do /myuxreview) — vem toda dos primitivos do Radix já usados
 * no diálogo de criação, e não de reimplementação:
 *   focus-trap, `role="dialog"` + `aria-modal`, Esc = Cancelar e foco de
 *   retorno ao botão de origem são do `DialogContent`. O que este componente
 *   acrescenta: autofoco no campo, `inputmode="numeric"` +
 *   `autocomplete="one-time-code"` (teclado numérico e preenchimento do código
 *   pelo SO), label ASSOCIADA por `htmlFor`, e o erro em `aria-live="polite"`
 *   (o leitor de tela anuncia sem interromper o que o usuário está digitando).
 */
export function StepUpDialog({ open, onOpenChange, onSubmit, submitting, error }) {
  const [code, setCode] = useState("");
  const inputRef = useRef(null);
  const codeId = useId();
  const errorId = `${codeId}-erro`;

  // O código NÃO sobrevive ao fechamento: reabrir o modal (inclusive no
  // caminho C6, prova expirada) tem de pedir o código ATUAL do app — um
  // código antigo pré-preenchido só serviria para falhar de novo.
  useEffect(() => {
    if (!open) setCode("");
  }, [open]);

  const pronto = code.length === TOTP_CODE_LENGTH && !submitting;

  const confirmar = () => {
    if (!pronto) return;
    onSubmit(code);
    // O campo esvazia no ATO: um TOTP é de uso único e vale ~30s. Deixar o
    // código anterior na tela junto de "digite o código atual" (C6) convida a
    // reenviar exatamente o que acabou de ser recusado.
    setCode("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="token-stepup-dialog"
        // EXPLÍCITO: o Radix marca o resto da árvore com `aria-hidden` mas
        // NÃO emite `aria-modal` (verificado no dist do pacote). Sem o par
        // `role="dialog"` + `aria-modal`, leitor de tela que não honra o
        // aria-hidden segue navegando o conteúdo de trás.
        aria-modal="true"
        // Autofoco no campo (e não no primeiro botão): quem chega aqui já
        // decidiu emitir e só falta digitar.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" aria-hidden="true" />
            {COPY_STEPUP.title}
          </DialogTitle>
          <DialogDescription>{COPY_STEPUP.body}</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            confirmar();
          }}
        >
          <Label htmlFor={codeId} className="text-[13px] font-medium">
            {COPY_STEPUP.codeLabel}
          </Label>
          <Input
            id={codeId}
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(normalizeTotpInput(e.target.value))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={TOTP_CODE_LENGTH}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className="font-mono-num tracking-[0.35em] text-lg"
            placeholder="000000"
            data-testid="token-stepup-code"
          />
          {/* O container do erro existe SEMPRE no DOM: um `aria-live` que só
              aparece junto com o texto costuma não ser anunciado, porque a
              região nasce já preenchida. */}
          <p
            id={errorId}
            aria-live="polite"
            className={`text-xs text-red-700 min-h-[1rem] ${error ? "" : "sr-only"}`}
            data-testid="token-stepup-error"
          >
            {error || ""}
          </p>
          {/* Submit oculto: Enter no campo confirma, sem depender de clique. */}
          <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
        </form>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="token-stepup-cancel"
          >
            {COPY_STEPUP.cancelCta}
          </Button>
          <Button onClick={confirmar} disabled={!pronto} data-testid="token-stepup-confirm">
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />}
            {COPY_STEPUP.confirmCta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StepUpDialog;
